import { useEffect, useState } from 'react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { getStripePromise } from '@/lib/stripe'
import { api } from '@/lib/api'
import { useBrand } from '@/hooks/useBrand'

export interface UpdatePaymentMethodModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function UpdatePaymentMethodModal({ open, onOpenChange, onSuccess }: UpdatePaymentMethodModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [initError, setInitError] = useState<string | null>(null)
  const [initializing, setInitializing] = useState(false)

  useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setInitError(null)
      return
    }

    let cancelled = false
    setInitializing(true)
    setInitError(null)

    api
      .post('/api/billing/payment-method/update/init')
      .then((res) => {
        if (cancelled) return
        setClientSecret(res.data.data.clientSecret)
      })
      .catch((err) => {
        if (cancelled) return
        setInitError(err.response?.data?.message ?? err.message ?? 'Failed to start update')
      })
      .finally(() => {
        if (!cancelled) setInitializing(false)
      })

    return () => {
      cancelled = true
    }
  }, [open])

  const options: StripeElementsOptions | undefined = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#4d71fd',
            colorDanger: '#ef4444',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            borderRadius: '10px',
          },
        },
      }
    : undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading">Update payment method</DialogTitle>
          <DialogDescription>
            Your new card will be used for future invoices, auto top-ups, and renewal charges.
          </DialogDescription>
        </DialogHeader>

        {initializing && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Preparing secure form…</span>
          </div>
        )}

        {initError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-600 dark:text-red-300">
            {initError}
          </div>
        )}

        {clientSecret && options && (
          <Elements stripe={getStripePromise()} options={options}>
            <UpdateForm onSuccess={onSuccess} onClose={() => onOpenChange(false)} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  )
}

function UpdateForm({ onSuccess, onClose }: { onSuccess: () => void; onClose: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const brand = useBrand()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)

    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/settings/billing',
      },
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(stripeError.message ?? 'Card setup failed')
      setSubmitting(false)
      return
    }

    if (!setupIntent?.payment_method) {
      setError('No payment method attached')
      setSubmitting(false)
      return
    }

    const paymentMethodId =
      typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method.id

    try {
      await api.post('/api/billing/payment-method/update/confirm', {
        setupIntentId: setupIntent.id,
        paymentMethodId,
      })
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.message ?? 'Failed to save new card')
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          // Brand the mandate text without changing the account-level
          // display name (shared with other products).
          business: { name: brand.appName },
        }}
      />
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !elements || submitting}
          className="flex-1 bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving…
            </>
          ) : (
            'Save new card'
          )}
        </Button>
      </div>
    </form>
  )
}
